import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { 
    FaArrowLeft
} from 'react-icons/fa';

const ZpHeadLotteryAllocationReport = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Determine if we are looking at Engineers or Societies based on the path
    const isSociety = location.pathname.includes('lottery-allocation-report-society');
    const pageTitle = isSociety ? "मजूर संख्या रिपोर्ट" : "सु.बे. रिपोर्ट";
    const reportHeading = isSociety ? "मजूर संख्या यादी" : "सु.बे. यादी";

    const [meetings, setMeetings] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [appliedDate, setAppliedDate] = useState('');
    const [loading, setLoading] = useState(true);
    
    // Data states
    const [generateWorkData, setGenerateWorkData] = useState({});
    const [winnersData, setWinnersData] = useState({});

    // Comprehensive Taluka Map with both English key and Marathi label
    const talukaMap = {
        'Shirala': 'शिराळा',
        'Walwa': 'वाळवा',
        'Tasgaon': 'तासगाव',
        'Khanapur': 'खानापूर (विटा)',
        'Atpadi': 'आटपाडी',
        'Kavathe Mahankal': 'कवठे महांकाळ',
        'Miraj': 'मिरज',
        'Palus': 'पलूस',
        'Jat': 'जत',
        'Kadegaon': 'कडेगाव',
        'Mukhyalaya': 'मुख्यालय'
    };

    /**
     * Standardizes taluka names to Marathi using the map.
     * Handles case-insensitivity for robustness.
     */
    const getMarathiTaluka = (name) => {
        if (!name) return "इतर";
        
        const searchName = name.trim();
        // Check exact match first
        if (talukaMap[searchName]) return talukaMap[searchName];
        
        // Check case-insensitive match
        const foundKey = Object.keys(talukaMap).find(
            key => key.toLowerCase() === searchName.toLowerCase()
        );
        
        return foundKey ? talukaMap[foundKey] : searchName;
    };

    useEffect(() => {
        setLoading(true);

        // 1. Fetch official lottery dates and work snapshots from generatework node
        const generateWorkRef = ref(db, 'ekamwatapzp/generatework');
        onValue(generateWorkRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                setGenerateWorkData(data);

                const dateList = Object.keys(data).map(key => ({
                    id: key,
                    date: key // Keys are already in 'DD-MM-YYYY' format
                }));
                // Sort dates descending (newest first)
                dateList.sort((a, b) => {
                    const dateA = new Date(a.date.split('-').reverse().join('-'));
                    const dateB = new Date(b.date.split('-').reverse().join('-'));
                    return dateB - dateA;
                });
                setMeetings(dateList);
            }
        });

        // 2. Fetch winner and payment data from GeneratedLottery
        const winnersRef = ref(db, 'ekamwatapzp/GeneratedLottery');
        onValue(winnersRef, (snapshot) => {
            if (snapshot.exists()) {
                setWinnersData(snapshot.val());
            }
            setLoading(false);
        });

    }, []);

    const handleSearch = () => {
        setAppliedDate(selectedDate);
    };

    // Aggregate Data by Taluka Logic
    const { aggregatedData, totals } = useMemo(() => {
        if (!appliedDate) return { aggregatedData: [], totals: {} };

        const stats = {};
        const uniqueInterestedByTaluka = {}; // Track unique user IDs per Taluka

        // 1. Initialize stats for all talukas to ensure every taluka appears in the report
        Object.values(talukaMap).forEach(talukaName => {
            stats[talukaName] = {
                taluka: talukaName,
                totalWorks: 0,
                totalInterestedCount: 0,
                totalAmount: 0,
                paidChallanCount: 0,
                remainingWorks: 0,
                remainingAmount: 0,
                plantWorks: 0,
                plantRemaining: 0,
                plantAmount: 0
            };
            uniqueInterestedByTaluka[talukaName] = new Set();
        });

        // 2. Process generatework for the selected lottery date
        // Robust traversal to find works either directly or nested in categories
        if (generateWorkData[appliedDate]) {
            const dateSnapshot = generateWorkData[appliedDate];
            
            const processWorkObject = (work) => {
                if (!work || typeof work !== 'object') return;
                
                // Only process if it has a schemeName (indicator of a work object)
                if (work.schemeName) {
                    const taluka = getMarathiTaluka(work.taluka);
                    
                    if (!stats[taluka]) {
                        stats[taluka] = {
                            taluka, totalWorks: 0, totalInterestedCount: 0, totalAmount: 0,
                            paidChallanCount: 0, remainingWorks: 0, remainingAmount: 0,
                            plantWorks: 0, plantRemaining: 0, plantAmount: 0
                        };
                        uniqueInterestedByTaluka[taluka] = new Set();
                    }

                    // Categorize work by Engineer or Society
                    const isWorkSociety = (work.category || '').toLowerCase().includes('society') || 
                                         (work.registrationType || '').toLowerCase().includes('society') ||
                                         (work.sharedwork || '').includes('मजूर'); // 'मजूर' corresponds to Societies
                    
                    // Logic check: Subbe (Engineer) vs Majur (Society)
                    // We check if report type matches work category
                    if (isSociety === isWorkSociety) {
                        const amount = parseFloat(work.tenderAmount || 0);
                        stats[taluka].totalWorks += 1;
                        stats[taluka].totalAmount += amount;

                        // Track unique interest from users
                        if (work.interesteduser) {
                            Object.keys(work.interesteduser).forEach(userId => {
                                uniqueInterestedByTaluka[taluka].add(userId);
                            });
                        }
                    }
                } else if (typeof work === 'object') {
                    // Recursive call for nested meeting/category structures
                    Object.values(work).forEach(subWork => processWorkObject(subWork));
                }
            };

            processWorkObject(dateSnapshot);
        }

        // 3. Process GeneratedLottery for winner payment status
        if (winnersData[appliedDate]) {
            const winnersInMeeting = winnersData[appliedDate];
            Object.values(winnersInMeeting).forEach(winner => {
                const taluka = getMarathiTaluka(winner.taluka || winner.Taluka);
                
                if (stats[taluka]) {
                    const isWinnerSociety = (winner.role || winner.category || '').toLowerCase().includes('society') || 
                                           (winner.registrationType || '').toLowerCase().includes('society');

                    if (isSociety === isWinnerSociety) {
                        if (winner.challanUploadUrl) {
                            stats[taluka].paidChallanCount += 1;
                        }
                    }
                }
            });
        }

        // 4. Calculate final derived fields and global totals
        const finalData = Object.values(stats);
        const totalsObj = {
            totalWorks: 0,
            totalInterestedCount: 0,
            totalAmount: 0,
            paidChallanCount: 0,
            remainingWorks: 0,
            remainingAmount: 0,
            plantWorks: 0,
            plantRemaining: 0,
            plantAmount: 0
        };

        finalData.forEach(data => {
            // Update interest count from the Set of unique IDs
            data.totalInterestedCount = uniqueInterestedByTaluka[data.taluka]?.size || 0;

            // Alignment with reference image: 
            // - "Remaining Works" usually stays as total count until fully processed/paid
            // - The image shows Shirala: works 3, remaining 3.
            data.remainingWorks = data.totalWorks; 
            data.remainingAmount = data.totalAmount;

            totalsObj.totalWorks += data.totalWorks;
            totalsObj.totalInterestedCount += data.totalInterestedCount;
            totalsObj.totalAmount += data.totalAmount;
            totalsObj.paidChallanCount += data.paidChallanCount;
            totalsObj.remainingWorks += data.remainingWorks;
            totalsObj.remainingAmount += data.remainingAmount;
            totalsObj.plantWorks += data.plantWorks;
            totalsObj.plantRemaining += data.plantRemaining;
            totalsObj.plantAmount += data.plantAmount;
        });

        return { aggregatedData: finalData, totals: totalsObj };
    }, [generateWorkData, winnersData, appliedDate, isSociety]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    if (loading) {
        return (
            <div className="p-20 flex flex-col items-center justify-center space-y-4 font-marathi">
                <div className="animate-spin text-[#22165f] text-4xl border-4 border-t-transparent border-gray-200 rounded-full w-12 h-12"></div>
                <p className="text-gray-400 font-bold animate-pulse uppercase tracking-widest text-xs">माहिती लोड होत आहे...</p>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 bg-gray-50/50 min-h-screen font-marathi">
            {/* Navigation Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4 px-2 no-print">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate(-1)}
                        className="p-2.5 bg-white rounded-xl shadow-sm border border-gray-100 text-gray-400 hover:text-[#22165f] transition active:scale-95"
                    >
                        <FaArrowLeft />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-800 tracking-tight">{reportHeading}</h1>
                </div>
                <div className="flex items-center gap-2 text-[12px] font-bold text-gray-400 uppercase tracking-wider">
                    सांगली जिल्हा परिषद / <span className="text-gray-600 font-black">{pageTitle}</span>
                </div>
            </div>

            {/* Selection Filter */}
            <div className="bg-white rounded shadow-sm border border-gray-100 p-8 mb-8 no-print">
                <div className="flex flex-col gap-3">
                    <label className="text-sm font-bold text-gray-700">
                        लॉटरीची तारीख निवडा <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-col md:flex-row gap-4 max-w-2xl">
                        <select 
                            className="flex-grow bg-white border border-gray-200 rounded py-2.5 px-4 focus:ring-2 focus:ring-blue-500/20 outline-none transition font-medium"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                        >
                            <option value="">तारीख निवडा</option>
                            {meetings.map(m => (
                                <option key={m.id} value={m.id}>{m.date}</option>
                            ))}
                        </select>
                        <button 
                            onClick={handleSearch}
                            className="bg-[#4e73df] hover:bg-blue-700 text-white px-10 py-2.5 rounded font-bold shadow-md transition transform active:scale-95 whitespace-nowrap"
                        >
                            Search
                        </button>
                    </div>
                </div>
            </div>

            {/* Professional Report Table */}
            <div className="bg-white rounded shadow-sm border border-gray-100 overflow-hidden relative pb-20">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse border border-gray-200">
                        <thead className="bg-[#f8f9fa] border-b border-gray-200">
                            <tr className="text-[11px] font-black text-gray-600">
                                <th className="px-2 py-4 border border-gray-200 text-center w-12">अ.क्र.</th>
                                <th className="px-2 py-4 border border-gray-200">तालुका</th>
                                <th className="px-2 py-4 border border-gray-200 text-center">एकूण कामे</th>
                                <th className="px-2 py-4 border border-gray-200 text-center">इच्छापत्र दर्शविलेले एकूण सुबेज्ञ</th>
                                <th className="px-2 py-4 border border-gray-200 text-center">एकूण रक्कम</th>
                                <th className="px-2 py-4 border border-gray-200 text-center">कामाचे चलन भरलेले एकूण सुबेज्ञ</th>
                                <th className="px-2 py-4 border border-gray-200 text-center">कामवाटपामधील उर्वरित कामे</th>
                                <th className="px-2 py-4 border border-gray-200 text-center">उर्वरित कामाची एकूण रक्कम</th>
                                <th className="px-2 py-4 border border-gray-200 text-center">प्लॅन्ट मधील एकूण कामे</th>
                                <th className="px-2 py-4 border border-gray-200 text-center">प्लॅन्ट कामवाटपामधील शिल्लक कामे</th>
                                <th className="px-2 py-4 border border-gray-200 text-center">एकूण रक्कम</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 text-[13px]">
                            {aggregatedData.map((item, index) => (
                                <tr key={index} className="hover:bg-blue-50/10 transition-colors">
                                    <td className="px-2 py-4 border border-gray-200 text-center font-bold text-gray-400">{index + 1}</td>
                                    <td className="px-2 py-4 border border-gray-200 font-bold text-gray-700">{item.taluka}</td>
                                    <td className="px-2 py-4 border border-gray-200 text-center font-bold text-gray-800">{item.totalWorks}</td>
                                    <td className="px-2 py-4 border border-gray-200 text-center font-bold text-gray-800">{item.totalInterestedCount}</td>
                                    <td className="px-2 py-4 border border-gray-200 text-center font-bold text-gray-700">{formatCurrency(item.totalAmount)}</td>
                                    <td className="px-2 py-4 border border-gray-200 text-center font-bold text-gray-800">{item.paidChallanCount}</td>
                                    <td className="px-2 py-4 border border-gray-200 text-center font-bold text-gray-800">{item.remainingWorks}</td>
                                    <td className="px-2 py-4 border border-gray-200 text-center font-bold text-gray-700">{formatCurrency(item.remainingAmount)}</td>
                                    <td className="px-2 py-4 border border-gray-200 text-center font-bold text-gray-400">0</td>
                                    <td className="px-2 py-4 border border-gray-200 text-center font-bold text-gray-400">0</td>
                                    <td className="px-2 py-4 border border-gray-200 text-center font-bold text-gray-400">0.00</td>
                                </tr>
                            ))}
                            {aggregatedData.length > 0 && (
                                <tr className="bg-[#f8f9fa] font-black text-gray-800">
                                    <td colSpan="2" className="px-2 py-4 border border-gray-200 text-right pr-6">Total</td>
                                    <td className="px-2 py-4 border border-gray-200 text-center">{totals.totalWorks}</td>
                                    <td className="px-2 py-4 border border-gray-200 text-center">{totals.totalInterestedCount}</td>
                                    <td className="px-2 py-4 border border-gray-200 text-center">{formatCurrency(totals.totalAmount)}</td>
                                    <td className="px-2 py-4 border border-gray-200 text-center">{totals.paidChallanCount}</td>
                                    <td className="px-2 py-4 border border-gray-200 text-center">{totals.remainingWorks}</td>
                                    <td className="px-2 py-4 border border-gray-200 text-center">{formatCurrency(totals.remainingAmount)}</td>
                                    <td className="px-2 py-4 border border-gray-200 text-center">0</td>
                                    <td className="px-2 py-4 border border-gray-200 text-center">0</td>
                                    <td className="px-2 py-4 border border-gray-200 text-center">0.00</td>
                                </tr>
                            )}
                            {aggregatedData.length === 0 && (
                                <tr>
                                    <td colSpan="11" className="px-6 py-24 text-center text-gray-300 font-bold italic bg-gray-50/30">
                                        माहिती उपलब्ध नाही. कृपया तारीख निवडून सर्च करा.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Print Action at Bottom */}
                {appliedDate && (
                    <div className="absolute bottom-6 left-0 right-0 flex justify-center no-print">
                        <button 
                            onClick={() => window.print()}
                            className="bg-[#4e73df] hover:bg-blue-700 text-white px-12 py-2.5 rounded font-bold shadow-lg transition active:scale-95 whitespace-nowrap"
                        >
                            Print
                        </button>
                    </div>
                )}
            </div>

            <style>{`
                .font-marathi {
                    font-family: 'Hind', sans-serif;
                }
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
                @media print {
                    .no-print { display: none !important; }
                    body { background: white !important; padding: 0 !important; }
                    .min-h-screen { min-height: auto !important; }
                    .p-4, .p-8 { padding: 0 !important; }
                    table { border: 1px solid #eee !important; width: 100% !important; border-collapse: collapse !important; }
                    th { background: #f8f9fa !important; color: #000 !important; border: 1px solid #ddd !important; }
                    td { border: 1px solid #ddd !important; }
                    .shadow-sm, .shadow-lg { shadow: none !important; }
                }
            `}</style>
        </div>
    );
};

export default ZpHeadLotteryAllocationReport;
